import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HiGlobeAlt, HiUserGroup, HiBell, HiChatBubbleLeftRight, HiFolder, HiSparkles, HiMinus, HiChatBubbleLeft, HiUsers, HiPlus, HiCog6Tooth, HiArrowsPointingOut, HiLockClosed, HiSignal } from 'react-icons/hi2';
import { useInfiniChat } from '@/hooks/useInfiniChat';
import { useFriendOnlineNotifications } from '@/hooks/useFriendOnlineNotifications';
import { UserProfilePopover } from './UserProfilePopover';
import { MentionAutocomplete } from './MentionAutocomplete';
import { FriendsModal } from './FriendsModal';
import { ClansPanel, ClanRecruitmentSlidePanel } from './ClansPanel';
import { EmailValidationBanner } from './EmailValidationBanner';
import { useAuthStore } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useKrakenChat } from '@/hooks/useKrakenChat';
import { parseMentions, convertMentionsToIds } from '@/utils/mentions';
import './InfiniChatWidget.css';

type Channel = 'global' | 'clan' | 'system' | 'dm' | 'projects' | 'guide' | 'broadcast';

// InfiniChat Size Presets
type ChatSize = 'normal' | 'large' | 'xlarge';
const CHAT_SIZES: Record<ChatSize, { width: number; minHeight: number; maxHeight: number; label: string }> = {
  normal: { width: 338, minHeight: 285, maxHeight: 510, label: 'Normal' },
  large: { width: 420, minHeight: 350, maxHeight: 650, label: 'Grande' },
  xlarge: { width: 520, minHeight: 400, maxHeight: 780, label: 'Extra Grande' },
};

interface InfiniChatWidgetProps {
  forceMinimized?: boolean;
}

export function InfiniChatWidget({ forceMinimized = false }: InfiniChatWidgetProps) {
  const { t } = useTranslation();
  // No mobile, não renderizar - o chat é acessado via MobileChatView
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!forceMinimized);
  const [message, setMessage] = useState('');
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Chat size settings (persisted in localStorage)
  const [chatSize, setChatSize] = useState<ChatSize>(() => {
    const saved = localStorage.getItem('infinichat-size');
    // Validate saved value against current presets (compact was removed)
    if (saved && saved in CHAT_SIZES) return saved as ChatSize;
    return 'normal';
  });
  const [height, setHeight] = useState(() => {
    const saved = localStorage.getItem('infinichat-height');
    return saved ? parseInt(saved, 10) : CHAT_SIZES[chatSize].minHeight;
  });
  const [showSizeOptions, setShowSizeOptions] = useState(false);

  // Clan recruitment modal (rendered outside InfiniChat)
  const [showClanRecruitmentModal, setShowClanRecruitmentModal] = useState(false);
  const [recruitmentPosts, setRecruitmentPosts] = useState<any[]>([]);

  // Message history navigation (like terminal)
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [tempMessage, setTempMessage] = useState('');

  // Mention autocomplete state
  const [showMentionAutocomplete, setShowMentionAutocomplete] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [userIdMap, setUserIdMap] = useState<Map<string, string>>(new Map());

  // Clan membership check (for input enabling in clan channel)
  const [isClanMember, setIsClanMember] = useState(false);

  // Persist chat size
  useEffect(() => {
    localStorage.setItem('infinichat-size', chatSize);
    // Adjust height to fit new size limits
    const limits = CHAT_SIZES[chatSize];
    setHeight(prev => Math.max(limits.minHeight, Math.min(limits.maxHeight, prev)));
  }, [chatSize]);

  useEffect(() => {
    localStorage.setItem('infinichat-height', height.toString());
  }, [height]);

  // Use WebSocket hook
  const {
    messages: currentMessages,
    isConnected,
    connectionError,
    currentChannel,
    sendMessage: sendChatMessage,
    joinChannel,
    addSystemMessage,
    rateLimitError,
  } = useInfiniChat();

  // Kraken AI tutor hook (for guide channel)
  const kraken = useKrakenChat();

  // Load Kraken history when switching to guide channel
  useEffect(() => {
    if (currentChannel === 'guide') {
      kraken.loadHistory('infinichat');
    }
  }, [currentChannel]);

  // Friend online notifications
  const handleFriendOnline = useCallback((notification: { friendName: string }) => {
    // Add system message to current channel
    addSystemMessage(`🟢 ${t('chat.friendOnline', { name: notification.friendName })}`);
  }, [addSystemMessage, t]);

  useFriendOnlineNotifications(handleFriendOnline);

  // Check clan membership (for input enabling in clan channel)
  const { token, user } = useAuthStore();
  useEffect(() => {
    if (!token) return;

    const checkClanMembership = async () => {
      try {
        const response = await fetch('https://mkey-api-production.fidelismadboat.workers.dev/clans/my', {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setIsClanMember(data.is_member || false);
        }
      } catch (error) {
        console.error('[InfiniChatWidget] Error checking clan membership:', error);
      }
    };

    checkClanMembership();
  }, [token]);

  // Channels config
  const channels = [
    { id: 'global' as Channel, icon: HiGlobeAlt, label: 'Global' },
    { id: 'clan' as Channel, icon: HiUserGroup, label: 'Clan' },
    { id: 'system' as Channel, icon: HiBell, label: 'System' },
    { id: 'dm' as Channel, icon: HiChatBubbleLeftRight, label: 'DM' },
    { id: 'projects' as Channel, icon: HiFolder, label: 'Projects' },
    { id: 'guide' as Channel, icon: HiSparkles, label: 'Kraken' },
    { id: 'broadcast' as Channel, icon: HiSignal, label: 'Broadcast' },
  ];

  const currentChannelData = channels.find(c => c.id === currentChannel);
  const CurrentIcon = currentChannelData?.icon;

  // Determine if input should be disabled based on current channel
  const isInputDisabled = (() => {
    if (rateLimitError) return true; // Always disable if rate limited

    switch (currentChannel) {
      case 'system':
        return true; // System messages are read-only
      case 'clan':
        return !isClanMember; // Enable only if user is in a clan
      case 'projects':
        return true; // Enable only when user has active project (TODO: implement project tracking)
      case 'guide':
        return false; // Kraken AI tutor — always enabled
      default:
        return false; // Global and DM channels are always enabled
    }
  })();

  // With column-reverse, newest messages are at the bottom automatically
  // No need for manual scrolling - the layout handles it!

  // Handle resize
  useEffect(() => {
    const resizeElement = resizeRef.current;
    if (!resizeElement) return;

    const onMouseDown = (e: MouseEvent) => {
      const startY = e.clientY;
      const startHeight = height;
      const limits = CHAT_SIZES[chatSize];

      const onMouseMove = (e: MouseEvent) => {
        const delta = startY - e.clientY; // Invertido (drag pra cima aumenta)
        const newHeight = Math.max(limits.minHeight, Math.min(limits.maxHeight, startHeight + delta));
        setHeight(newHeight);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      e.preventDefault();
    };

    resizeElement.addEventListener('mousedown', onMouseDown);

    return () => {
      resizeElement.removeEventListener('mousedown', onMouseDown);
    };
  }, [isExpanded]);

  // Minimize when forceMinimized prop changes to true
  useEffect(() => {
    if (forceMinimized) {
      setIsExpanded(false);
    }
  }, [forceMinimized]);

  // Early return AFTER all hooks (React rules of hooks)
  if (isMobile) return null;

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const trimmedMessage = message.trim();

    // Add to message history (max 50 messages)
    setMessageHistory(prev => {
      const newHistory = [...prev, trimmedMessage];
      return newHistory.slice(-50); // Keep last 50 messages
    });

    // Reset history navigation
    setHistoryIndex(-1);
    setTempMessage('');

    // Guide channel → Kraken AI (HTTP API, not WebSocket)
    if (currentChannel === 'guide') {
      kraken.sendMessage(trimmedMessage, 'infinichat');
      setMessage('');
      setShowMentionAutocomplete(false);
      return;
    }

    // Check if it's a whisper (starts with "username)
    // Format: "username message content
    const whisperMatch = trimmedMessage.match(/^"([a-zA-Z0-9_]+)\s+(.+)$/);

    if (whisperMatch) {
      const [, targetUsername, whisperContent] = whisperMatch;

      // Send whisper with special format
      // Backend will handle routing to specific user only
      sendChatMessage(`"${targetUsername} ${whisperContent}`, currentChannel);
    } else {
      // Convert @username to <@userId> for mentions before sending
      const messageWithIds = convertMentionsToIds(trimmedMessage, userIdMap);
      sendChatMessage(messageWithIds, currentChannel);
    }

    setMessage('');
    setShowMentionAutocomplete(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle Enter/Tab if autocomplete is showing
    if (showMentionAutocomplete && (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      return; // Let MentionAutocomplete handle it
    }

    // History navigation with arrow keys (like terminal)
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (messageHistory.length === 0) return;

      // Save current message when starting navigation
      if (historyIndex === -1) {
        setTempMessage(message);
      }

      const newIndex = historyIndex === -1
        ? messageHistory.length - 1
        : Math.max(0, historyIndex - 1);

      setHistoryIndex(newIndex);
      setMessage(messageHistory[newIndex]);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex === -1) return; // Not navigating

      const newIndex = historyIndex + 1;

      if (newIndex >= messageHistory.length) {
        // Reached the end, restore temp message
        setHistoryIndex(-1);
        setMessage(tempMessage);
      } else {
        setHistoryIndex(newIndex);
        setMessage(messageHistory[newIndex]);
      }
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }

    if (e.key === 'Escape') {
      setShowMentionAutocomplete(false);
      // Also reset history navigation on Escape
      if (historyIndex !== -1) {
        setHistoryIndex(-1);
        setMessage(tempMessage);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    // If user starts typing during history navigation, cancel navigation
    if (historyIndex !== -1) {
      setHistoryIndex(-1);
      setTempMessage('');
    }

    setMessage(newValue);

    // Check if user typed @ for mention autocomplete
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by space
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || lastAtIndex === 0) {
        const query = textBeforeCursor.substring(lastAtIndex + 1);

        // Check if query is valid (no spaces)
        if (!query.includes(' ')) {
          setMentionQuery(query);
          setMentionStartIndex(lastAtIndex);
          setShowMentionAutocomplete(true);

          // Calculate position for autocomplete dropdown
          if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setMentionPosition({
              top: rect.top - 300, // Show above input
              left: rect.left,
            });
          }
          return;
        }
      }
    }

    // Hide autocomplete if conditions not met
    setShowMentionAutocomplete(false);
  };

  const handleMentionSelect = (user: { id: string; username: string; display_name: string }) => {
    // Replace @query with @username in message
    const beforeMention = message.substring(0, mentionStartIndex);
    const afterMention = message.substring(message.indexOf(' ', mentionStartIndex) !== -1 ? message.indexOf(' ', mentionStartIndex) : message.length);

    const newMessage = `${beforeMention}@${user.username} ${afterMention}`.trim() + ' ';
    setMessage(newMessage);

    // Store userId for later conversion
    setUserIdMap(prev => new Map(prev).set(user.username, user.id));

    // Hide autocomplete
    setShowMentionAutocomplete(false);

    // Focus back on input
    inputRef.current?.focus();
  };

  // Handle username click for whisper/DM
  const handleUsernameClick = useCallback((username: string) => {
    // Set message input to "username " for whisper
    setMessage(`"${username} `);
    // Focus input
    inputRef.current?.focus();
  }, []);

  if (!isExpanded) {
    return (
      <div className="infinichat-minimized" onClick={() => setIsExpanded(true)}>
        <HiChatBubbleLeft size={18} />
        <div className="minimized-badge">3</div>
      </div>
    );
  }

  return (
    <div
      ref={chatRef}
      className={`infinichat-widget size-${chatSize}`}
      style={{ height: `${height}px`, width: `${CHAT_SIZES[chatSize].width}px` }}
      data-channel={currentChannel}
    >
      {/* Resize Handle */}
      <div ref={resizeRef} className="resize-handle" title={t('chat.resizeTooltip')}>
        <div className="resize-indicator"></div>
      </div>

      {/* Tabs */}
      <div className="infinichat-tabs">
        {channels.map(channel => {
          const Icon = channel.icon;
          return (
            <button
              key={channel.id}
              className={`tab-button ${currentChannel === channel.id ? 'active' : ''}`}
              onClick={() => joinChannel(channel.id)}
              title={channel.label}
            >
              <Icon size={14} />
            </button>
          );
        })}

        <div className="tab-spacer"></div>

        <button
          className="tab-control"
          onClick={() => setShowFriendsModal(true)}
          title={t('chat.friendsTooltip')}
        >
          <HiUsers size={12} />
        </button>

        {/* Size Options Toggle */}
        <div className="size-options-container">
          <button
            className={`tab-control ${showSizeOptions ? 'active' : ''}`}
            onClick={() => setShowSizeOptions(!showSizeOptions)}
            title={t('chat.chatSizeTooltip')}
          >
            <HiCog6Tooth size={12} />
          </button>
          {showSizeOptions && (
            <div className="size-options-dropdown">
              {(Object.keys(CHAT_SIZES) as ChatSize[]).map((size) => (
                <button
                  key={size}
                  className={`size-option ${chatSize === size ? 'active' : ''}`}
                  onClick={() => { setChatSize(size); setShowSizeOptions(false); }}
                >
                  {size === 'normal' ? <HiCog6Tooth size={14} /> : <HiArrowsPointingOut size={14} />}
                  <span>{size === 'normal' ? t('chat.sizeNormal') : size === 'large' ? t('chat.sizeLarge') : t('chat.sizeXLarge')}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          className="tab-control"
          onClick={() => setIsExpanded(false)}
          title={t('chat.minimizeTooltip')}
        >
          <HiMinus size={12} />
        </button>
      </div>

      {/* Email Validation Banner */}
      <EmailValidationBanner />

      {/* Messages Area */}
      <div className="infinichat-messages">
        {!isConnected && (
          <div className="connection-status">
            {connectionError || t('chat.connecting')}
          </div>
        )}

        {/* Show ClansPanel when clan channel is active */}
        {currentChannel === 'clan' ? (
          <ClansPanel
            userTitle={useAuthStore.getState().user?.odyssey_title || 'Tripulante'}
            userLevel={useAuthStore.getState().user?.odyssey_level || 1}
            onOpenRecruitmentModal={(posts) => {
              setRecruitmentPosts(posts);
              setShowClanRecruitmentModal(true);
            }}
          />
        ) : currentChannel === 'projects' ? (
          <div className="locked-channel-panel projects">
            <div className="locked-channel-icon">
              <div className="icon-glow" />
              <div className="icon-ring" />
              <HiFolder size={28} />
            </div>
            <span className="locked-badge">
              <HiLockClosed size={10} /> {t('chat.channelProjects.badge')}
            </span>
            <h4 className="locked-channel-title">{t('chat.channelProjects.title')}</h4>
            <p className="locked-channel-desc">
              {t('chat.channelProjects.desc')}
            </p>
            <div className="locked-channel-separator" />
            <p className="locked-channel-hint">{t('chat.channelProjects.hint')}</p>
          </div>
        ) : currentChannel === 'guide' ? (
          <>
            {kraken.messages.length === 0 && !kraken.isTyping ? (
              <div className="kraken-welcome">
                <div className="kraken-welcome-icon">
                  <div className="icon-glow" />
                  <HiSparkles size={32} />
                </div>
                <h4 className="kraken-welcome-title">{t('chat.channelGuide.title')}</h4>
                <p className="kraken-welcome-desc">{t('chat.channelGuide.desc')}</p>
              </div>
            ) : (
              <>
                {kraken.messages.map(msg => (
                  <div key={msg.id} className={`message-item ${msg.role === 'assistant' ? 'kraken-message' : ''}`}>
                    <span className={`message-username ${msg.role === 'assistant' ? 'kraken-sender' : ''}`}>
                      {msg.role === 'assistant' ? '🐙 Kraken' : (user?.display_name || 'Você')}:
                    </span>
                    <span className="message-content">{msg.content}</span>
                    {msg.role === 'assistant' && !msg.isLoading && (
                      <span className="kraken-rating">
                        <button
                          className={`kraken-rate-btn ${msg.rating === 5 ? 'active' : ''}`}
                          onClick={() => kraken.rateMessage(msg.id, 5)}
                          title="Boa resposta"
                        >👍</button>
                        <button
                          className={`kraken-rate-btn ${msg.rating === 1 ? 'active' : ''}`}
                          onClick={() => kraken.rateMessage(msg.id, 1)}
                          title="Resposta ruim"
                        >👎</button>
                      </span>
                    )}
                  </div>
                ))}
                {kraken.isTyping && (
                  <div className="message-item kraken-message kraken-typing">
                    <span className="kraken-sender">🐙 Kraken:</span>
                    <span className="message-content kraken-typing-dots">
                      {t('chat.kraken.typing')}
                    </span>
                  </div>
                )}
              </>
            )}
            {kraken.error && (
              <div className="kraken-error">
                {kraken.error}
              </div>
            )}
            {kraken.remainingMessages <= 5 && kraken.remainingMessages > 0 && (
              <div className="kraken-remaining">
                {t('chat.kraken.remaining', { count: kraken.remainingMessages })}
              </div>
            )}
          </>
        ) : currentMessages.length === 0 ? (
          <div className="messages-empty">
            {CurrentIcon && <CurrentIcon size={36} className="empty-icon" />}
            <div className="empty-text">{t('chat.noMessages', { channel: currentChannelData?.label })}</div>
          </div>
        ) : (
          <>
            {[...currentMessages].reverse().map(msg => {
              const contentParts = parseMentions(msg.content);
              const isWhisper = (msg as any).isWhisper; // Check if message is a whisper
              const whisperTarget = (msg as any).whisperTarget; // Target username for whisper
              const isSystemMessage = msg.userId === 'system';
              const isKraken = isSystemMessage && msg.username === 'Kraken';

              // System messages get special styling with clickable mentions
              if (isSystemMessage) {
                return (
                  <div key={msg.id} className={`message-item system-message ${isKraken ? 'kraken-message' : ''}`}>
                    <span className={`system-sender ${isKraken ? 'kraken-sender' : ''}`}>
                      {isKraken ? '🐙' : '🔔'} {msg.username}:
                    </span>
                    <span className="message-content system-content">{contentParts}</span>
                  </div>
                );
              }

              return (
                <div key={msg.id} className={`message-item ${isWhisper ? 'whisper' : ''}`}>
                  <UserProfilePopover
                    userId={msg.userId}
                    username={msg.username}
                    onUsernameClick={handleUsernameClick}
                  >
                    <span className="message-username">{msg.username}:</span>
                  </UserProfilePopover>
                  {isWhisper && whisperTarget && (
                    <span className="whisper-target">@{whisperTarget}</span>
                  )}
                  <span className="message-content">{contentParts}</span>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Mention Autocomplete */}
      {showMentionAutocomplete && (
        <MentionAutocomplete
          query={mentionQuery}
          onSelect={handleMentionSelect}
          position={mentionPosition}
        />
      )}

      {/* Input Area */}
      <form className="infinichat-input" onSubmit={handleSendMessage}>
        <span className="input-channel">
          [{currentChannelData?.label}]
        </span>
        <input
          ref={inputRef}
          type="text"
          className="input-field"
          placeholder={
            isInputDisabled && currentChannel === 'system'
              ? t('chat.placeholderSystem')
              : isInputDisabled && currentChannel === 'clan'
              ? t('chat.placeholderClanNoMember')
              : isInputDisabled && currentChannel === 'projects'
              ? t('chat.placeholderProjects')
              : currentChannel === 'guide'
              ? t('chat.kraken.placeholder')
              : t('chat.placeholderDefault')
          }
          value={message}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isInputDisabled}
        />
        {rateLimitError && (
          <div className="rate-limit-error">
            ⚠️ {rateLimitError}
          </div>
        )}
      </form>

      {/* Friends Modal */}
      <FriendsModal
        isOpen={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
      />

      {/* Clan Recruitment Modal - Rendered OUTSIDE InfiniChat as extension */}
      {showClanRecruitmentModal && (
        <ClanRecruitmentSlidePanel
          posts={recruitmentPosts}
          onClose={() => setShowClanRecruitmentModal(false)}
          chatWidth={CHAT_SIZES[chatSize].width}
        />
      )}
    </div>
  );
}
